import { useEffect, useState } from 'react'
import { Link } from '../router.js'
import { Combobox } from '../admin/pickers.js'
import { lireSupervision, type Lecture } from '../supervision/api.js'
import { cheminAppareil } from '../supervision/vues/fiches/modele.js'
import {
  CHEMIN_CAMERAS_SONDE, estListeCameras, choixCameras, cameraDansListe, salleCamera, DOT_ETAT,
  type ListeCameras,
} from './camera-porte-modele.js'

/**
 * CAMÉRA DE LA PORTE — le panneau de la fiche porte (24/10/2026).
 *
 * La caméra du registre de Sonde qui filme la porte. Ce composant n'écrit
 * RIEN : il montre le lien et propose un choix ; l'enregistrement passe par la
 * Save Bar unique de la fiche (avec le nom Gestion), qui appelle
 * PUT /api/v1/access/doors/:id/camera.
 *
 * La liste des caméras se lit UNE fois au montage, par le relais des vues de
 * supervision (droit sonde.read) : pas de sondage répété. Quatre cas :
 *   1. Gérer les portes + Consulter la supervision : le sélecteur ;
 *   2. Consulter la supervision seulement : la caméra telle que Sonde la décrit ;
 *   3. sans Consulter la supervision : l'identifiant seul (retrait possible
 *      avec Gérer les portes, puisqu'il n'appelle pas Sonde) ;
 *   4. aucune caméra et aucun droit de choix : une phrase.
 * Aucune image ni vidéo n'entre dans Gestion.
 */

const INDICE_SOURCE = 'La liste vient du registre de Sonde (caméras en service). Gestion ne garde que le lien : aucune image ni vidéo n’entre dans Gestion.'
const INDICE_SANS_SONDE = 'Le nom et l’état de la caméra se lisent dans la supervision : il faut le droit « Consulter la supervision ».'
const INDICE_CHOIX_SANS_SONDE = 'Choisir la caméra demande aussi le droit « Consulter la supervision ».'

export function SectionCameraPorte({ lien, valeur, onChange, peutGerer, peutSonde }: {
  /** La caméra enregistrée (serveur), ou null. */
  lien: { uid: string; updatedAt: string } | null
  /** La valeur en cours d'édition (Save Bar). */
  valeur: string | null
  onChange: (uid: string | null) => void
  /** access.doors.manage */
  peutGerer: boolean
  /** sonde.read */
  peutSonde: boolean
}) {
  const [lecture, setLecture] = useState<Lecture<ListeCameras> | null>(null)
  const [essai, setEssai] = useState(0)
  const aLire = peutSonde && (peutGerer || lien !== null)

  useEffect(() => {
    if (!aLire) return
    let vivant = true
    setLecture(null)
    void lireSupervision(CHEMIN_CAMERAS_SONDE, estListeCameras).then((r) => { if (vivant) setLecture(r) })
    return () => { vivant = false }
  }, [aLire, essai])

  const items = lecture?.etat === 'ok' ? lecture.data.items : null
  const lieUid = lien?.uid ?? null
  const voir = lien && peutSonde
    ? <p className="lc-hint"><Link href={cheminAppareil(lien.uid)} className="ac-seeall">Voir la caméra dans la supervision →</Link></p>
    : null

  let corps
  if (peutGerer && peutSonde) {
    // Cas 1 : le choix. Pendant la lecture ou après un échec, seule la caméra liée reste (retrait possible).
    const choix = choixCameras(items, lieUid, lecture === null ? 'lecture' : 'echec')
    corps = (<>
      <div className="ac-field"><span>Caméra qui filme cette porte</span>
        <Combobox value={valeur} items={choix} onChange={onChange} clearable placeholder="Aucune caméra" ariaLabel="Caméra qui filme cette porte" />
      </div>
      {lecture === null ? <p className="lc-hint">Lecture des caméras de Sonde…</p> : null}
      {lecture !== null && lecture.etat !== 'ok' ? (
        <p className="lc-hint ac-err">{lecture.message} Seul le retrait du lien est possible pour l’instant.{' '}
          <button type="button" className="lc-btn" onClick={() => setEssai((n) => n + 1)}>Relire la liste</button>
        </p>
      ) : null}
      {items !== null && choixCameras(items, null).length === 0 ? <p className="lc-hint">Aucune caméra en service au registre de Sonde.</p> : null}
      {lecture?.etat === 'ok' && lecture.data.next_cursor ? <p className="lc-hint">La liste de Sonde s’arrête à 500 caméras : cherchez par nom ou par identifiant.</p> : null}
      <p className="lc-hint">{INDICE_SOURCE}</p>
    </>)
  } else if (lien && peutSonde) {
    // Cas 2 : lecture seule, la caméra telle que Sonde la décrit.
    const c = cameraDansListe(items, lien.uid)
    corps = (
      <dl className="ac-dl">
        <div><dt>Caméra</dt><dd>{c ? c.display_name : lecture === null ? 'Lecture…' : items ? 'Inconnue de Sonde' : '—'}</dd></div>
        <div><dt>Salle</dt><dd>{c ? salleCamera(c.zone_code) : '—'}</dd></div>
        <div><dt>Identifiant Sonde</dt><dd className="ac-mono">{lien.uid}</dd></div>
        <div><dt>État</dt><dd>{c ? <><span className={`lc-dot ${DOT_ETAT[c.state.display_state] ?? 'lc-dot--idle'}`} aria-hidden="true" /> {c.state.display_label}</> : '—'}</dd></div>
      </dl>
    )
  } else if (lien) {
    // Cas 3 : sans le droit de la supervision, l'identifiant seul.
    corps = (<>
      {peutGerer ? (
        <div className="ac-field"><span>Caméra qui filme cette porte</span>
          <Combobox value={valeur} items={choixCameras(null, lien.uid, 'sans-droit')} onChange={onChange} clearable placeholder="Aucune caméra" ariaLabel="Caméra qui filme cette porte" />
        </div>
      ) : (
        <dl className="ac-dl"><div><dt>Caméra</dt><dd className="ac-mono">{lien.uid}</dd></div></dl>
      )}
      <p className="lc-hint">{INDICE_SANS_SONDE}</p>
      {peutGerer ? <p className="lc-hint">{INDICE_CHOIX_SANS_SONDE}</p> : null}
    </>)
  } else {
    // Cas 4 : aucune caméra, et pas de quoi en choisir une.
    corps = (<>
      <p className="lc-hint">Aucune caméra reliée à cette porte.</p>
      {peutGerer ? <p className="lc-hint">{INDICE_CHOIX_SANS_SONDE}</p> : null}
    </>)
  }

  return (
    <section className="ac-panel">
      <h2 className="lc-section-title">Caméra de la porte</h2>
      {corps}
      {voir}
    </section>
  )
}
